import React, { useState, useRef, useEffect } from 'react';
import { Table, Button, Upload, Space, Modal, Input, message, Popconfirm, Tooltip } from 'antd';
import { PlusOutlined, UploadOutlined, EditOutlined, DeleteOutlined,
  FileTextOutlined, ScanOutlined, CheckCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import StudentSelector from './StudentSelector';
import StatusTag from './StatusTag';
import ProgressDisplay from './ProgressDisplay';
import { essayAPI, classAPI, studentAPI } from '../services/api';
import { ProgressManager } from '../services/progressManager';
import {
  buildFilters,
  compareEssayStatus,
  compareText,
  createValueFilter,
  getEssayStatusLabel,
} from '../utils/tableHelpers';

const { TextArea } = Input;

interface StudentEssay {
  rowId?: string;
  id?: number;
  studentId: number;
  studentName: string;
  className: string;
  classId: number;
  file?: File;
  fileName?: string;
  status: 'pending' | 'uploaded' | 'recognizing' | 'recognized' | 'correcting' | 'completed' | 'failed';
  ocrText?: string;
  progress?: number;
  currentStage?: string;
  stageMessage?: string;
  error?: string;
}

interface Class {
  id: number;
  name: string;
  grade: number;
  student_count: number;
}

interface Student {
  id: number;
  name: string;
  class_id: number;
  class_name?: string;
  class?: {
    id: number;
    name: string;
    grade?: number;
  };
}

interface BatchInfo {
  batchId?: string;
  topicId?: number;
  topic?: any;
  essayType: 'continuation' | 'application';
}

interface StudentEssayTableProps {
  batchInfo: BatchInfo;
  essays: StudentEssay[];
  onEssaysChange: (essays: StudentEssay[] | ((prev: StudentEssay[]) => StudentEssay[])) => void;
  onBatchCreated?: (batchId: string) => void;
}

const StudentEssayTable: React.FC<StudentEssayTableProps> = ({
  batchInfo,
  essays,
  onEssaysChange,
  onBatchCreated
}) => {
  const navigate = useNavigate();
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingEssay, setEditingEssay] = useState<StudentEssay | null>(null);
  const [editingText, setEditingText] = useState('');
  // const [loading, setLoading] = useState(false);
  const [defaultClassId, setDefaultClassId] = useState<number | undefined>();
  const [classes, setClasses] = useState<Class[]>([]);
  const [studentsCache, setStudentsCache] = useState<Map<number, Student[]>>(new Map());
  const [loadingClasses, setLoadingClasses] = useState(false);
  const progressManagersRef = useRef<Map<number, ProgressManager>>(new Map());
  const pollingInterval = useRef<NodeJS.Timeout | null>(null);
  const rowIdCounterRef = useRef(0);

  /**
   * 为前端新增行生成稳定 ID，避免排序后按索引误操作。
   */
  const createRowId = () => `draft-row-${Date.now()}-${rowIdCounterRef.current++}`;

  /**
   * 获取表格行的稳定 ID。
   *
   * @param essay 当前作文行
   * @param fallbackIndex 缺少 rowId 时的兜底索引
   * @returns 当前行的稳定标识
   */
  const getEssayRowId = (essay: StudentEssay, fallbackIndex?: number) => (
    essay.rowId || (essay.id ? `essay-${essay.id}` : `draft-fallback-${fallbackIndex ?? 'missing'}`)
  );

  /**
   * 通过稳定 ID 反查原始数组索引，保证排序/筛选后仍能命中正确数据。
   *
   * @param rowId 表格稳定行 ID
   * @returns 原始 essays 数组中的索引
   */
  const findEssayIndex = (rowId: string) => (
    essays.findIndex((essay, index) => getEssayRowId(essay, index) === rowId)
  );


  // 组件mount时加载班级列表
  useEffect(() => {
    loadClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 加载班级列表（只执行一次）
  const loadClasses = async () => {
    if (classes.length > 0 || loadingClasses) return; // 避免重复加载
    setLoadingClasses(true);
    try {
      const response = await classAPI.list({ page_size: 100 });
      setClasses(response.data.data || []);
    } catch (error) {
      message.error('加载班级列表失败');
    } finally {
      setLoadingClasses(false);
    }
  };

  // 加载班级学生（带缓存）
  const loadClassStudents = async (classId: number): Promise<Student[]> => {
    // 如果已有缓存，直接返回
    if (studentsCache.has(classId)) {
      return studentsCache.get(classId) || [];
    }
    
    try {
      const response = await studentAPI.list({ 
        class_id: classId,
        page_size: 100 
      });
      const students = response.data.data || [];
      
      // 更新缓存
      setStudentsCache(prev => {
        const newCache = new Map(prev);
        newCache.set(classId, students);
        return newCache;
      });
      return students;
    } catch (error) {
      message.error('加载学生列表失败');
      return [];
    }
  };

  // 添加学生行
  const handleAddStudent = () => {
    // 使用默认班级ID或从第一个有班级的学生获取
    const classIdToUse = defaultClassId || essays.find(e => e.classId > 0)?.classId || 0;
    const classNameToUse = essays.find(e => e.classId === classIdToUse)?.className || '';
    
    const newEssay: StudentEssay = {
      rowId: createRowId(),
      studentId: 0,
      studentName: '',
      className: classNameToUse,
      classId: classIdToUse,
      status: 'pending'
    };
    onEssaysChange([...essays, newEssay]);
  };

  // 删除学生行
  const handleDeleteStudent = (rowId: string) => {
    onEssaysChange(essays.filter((essay, index) => getEssayRowId(essay, index) !== rowId));
    setSelectedRowIds((prevRowIds) => prevRowIds.filter((currentRowId) => currentRowId !== rowId));
  };

  // 选择学生
  const handleStudentSelect = (rowId: string, value: any) => {
    const index = findEssayIndex(rowId);

    if (index < 0) {
      return;
    }

    // 检查是否重复选择
    const existingStudent = essays.find((e, i) => 
      i !== index && e.studentId === value.studentId && e.studentId > 0
    );
    
    if (existingStudent) {
      message.warning(`学生 ${value.studentName} 已经添加过了`);
      return;
    }
    
    const newEssays = [...essays];
    newEssays[index] = {
      ...newEssays[index],
      studentId: value.studentId,
      studentName: value.studentName || value.student_name,
      className: value.className || value.class_name || '',
      classId: value.classId || value.class_id,
      // 保留原有的文件和状态（如果是修改学生）
      file: newEssays[index].file,
      fileName: newEssays[index].fileName,
      status: newEssays[index].status || 'pending',
      ocrText: newEssays[index].ocrText,
      progress: newEssays[index].progress,
      error: newEssays[index].error
    };
    
    // 如果是第一个选择的学生，记录其班级作为默认值
    if (!defaultClassId && value.classId > 0) {
      setDefaultClassId(value.classId);
    }
    
    onEssaysChange(newEssays);
  };
  
  // 修改学生
  const handleModifyStudent = (rowId: string) => {
    const index = findEssayIndex(rowId);

    if (index < 0) {
      return;
    }

    const newEssays = [...essays];
    // 保留班级信息和其他数据，但允许重新选择学生
    const currentEssay = newEssays[index];
    newEssays[index] = {
      ...currentEssay,
      studentId: 0,  // 设为0以显示选择器
      studentName: '', // 清空学生名称
      // 保留班级信息和其他数据
      classId: currentEssay.classId,
      className: currentEssay.className
    };
    onEssaysChange(newEssays);
  };

  // 创建作文记录
  const createEssayIfNeeded = async (essay: StudentEssay): Promise<number> => {
    if (essay.id) {
      return essay.id;
    }

    // 如果还没有批次ID，先批量创建
    if (!batchInfo.batchId) {
      const studentIds = essays
        .filter(e => e.studentId > 0)
        .map(e => e.studentId);
      
      if (studentIds.length === 0) {
        message.error('请先添加学生');
        throw new Error('No students');
      }

      try {
        const response = await essayAPI.batchCreate({
          topic_id: batchInfo.topicId!,
          essay_type: batchInfo.essayType,
          student_ids: studentIds
        });

        const { batch_id, essays: createdEssays } = response.data;
        
        // 更新批次ID
        if (onBatchCreated) {
          onBatchCreated(batch_id);
        }

        // 更新所有作文的ID
        const newEssays = essays.map(e => {
          const created = createdEssays.find((ce: any) => ce.student_id === e.studentId);
          if (created) {
            return { ...e, id: created.id };
          }
          return e;
        });
        onEssaysChange(newEssays);

        // 返回当前作文的ID
        const currentCreated = createdEssays.find((ce: any) => ce.student_id === essay.studentId);
        return currentCreated?.id || 0;
      } catch (error) {
        message.error('创建作文失败');
        throw error;
      }
    }

    // 如果已有批次ID但当前作文没有ID，说明是新添加的学生
    try {
      const response = await essayAPI.createWithStudent({
        topic_id: batchInfo.topicId!,
        essay_type: batchInfo.essayType,
        student_id: essay.studentId,
        class_id: essay.classId
      });
      return response.data.essay.id;
    } catch (error) {
      message.error('创建作文失败');
      throw error;
    }
  };

  // 文件上传 - 完全依赖后端状态
  const handleFileUpload = async (rowId: string, file: File) => {
    const index = findEssayIndex(rowId);

    if (index < 0) {
      return;
    }

    const essay = essays[index];

    try {
      // 先创建作文记录
      const essayId = await createEssayIfNeeded(essay);

      // 上传文件
      const uploadResponse = await essayAPI.upload(essayId, file);
      console.log('上传响应:', uploadResponse.data);

      // 等待一下确保数据库更新完成
      await new Promise(resolve => setTimeout(resolve, 500));

      // 从后端获取最新状态
      const essayData = await essayAPI.get(essayId);
      const updatedEssay = essayData.data.essay;
      console.log('获取的作文数据:', updatedEssay);

      // 根据后端返回的实际状态更新前端显示
      const newEssays = [...essays];
      // 从后端路径中提取文件名，或使用原始文件名
      let displayFileName = file.name;
      if (updatedEssay.original_file) {
        // 如果后端返回的是路径，提取最后的文件名部分
        const pathParts = updatedEssay.original_file.split('/');
        const backendFileName = pathParts[pathParts.length - 1];
        // 如果后端文件名包含下划线前缀（如 1_3_），尝试保留原始文件名
        displayFileName = file.name || backendFileName;
      }

      newEssays[index] = {
        ...newEssays[index],
        id: essayId,
        fileName: displayFileName,  // 使用处理后的文件名
        status: mapBackendStatusToFrontend(updatedEssay.status),
        ocrText: updatedEssay.corrected_text || updatedEssay.original_text || '',
        file: file // 仅用于前端显示
      };
      onEssaysChange(newEssays);

      if (updatedEssay.original_file) {
        message.success('文件上传成功');

        // 如果后端已经提取了文本
        if (updatedEssay.original_text || updatedEssay.corrected_text) {
          message.success('文本已自动提取');
        }
      } else {
        throw new Error('文件未成功保存');
      }
    } catch (error: any) {
      console.error('文件上传失败:', error);

      // 即使失败也要从后端获取最新状态
      try {
        if (essay.id) {
          const essayData = await essayAPI.get(essay.id);
          const updatedEssay = essayData.data.essay;

          const newEssays = [...essays];
          newEssays[index] = {
            ...newEssays[index],
            status: mapBackendStatusToFrontend(updatedEssay.status),
            error: error.message || '上传失败'
          };
          onEssaysChange(newEssays);
        }
      } catch {
        // 如果获取状态也失败，只更新错误信息
        const newEssays = [...essays];
        newEssays[index] = {
          ...newEssays[index],
          status: 'failed',
          error: error.message || '上传失败'
        };
        onEssaysChange(newEssays);
      }

      message.error(`文件上传失败: ${error.message || '未知错误'}`);
    }
  };

  // 将后端状态映射到前端显示状态
  const mapBackendStatusToFrontend = (backendStatus: string): StudentEssay['status'] => {
    const statusMap: Record<string, StudentEssay['status']> = {
      'pending': 'pending',
      'uploaded': 'uploaded',
      'recognizing': 'recognizing',
      'recognized': 'recognized',
      'correcting': 'correcting',
      'completed': 'completed',
      'failed': 'failed'
    };

    // 处理后端的processing状态，映射到前端的uploaded状态
    if (backendStatus === 'processing') {
      return 'uploaded';
    }

    return statusMap[backendStatus] || 'pending';
  };

  // 单个OCR识别 - 完全依赖后端状态
  const handleOCR = async (rowId: string) => {
    const index = findEssayIndex(rowId);

    if (index < 0) {
      return;
    }

    const essay = essays[index];

    if (!essay.id) {
      message.error('请先上传文件');
      return;
    }

    // 立即更新状态为识别中
    onEssaysChange(prevEssays => {
      const newEssays = [...prevEssays];
      newEssays[index] = {
        ...newEssays[index],
        status: 'recognizing',
        progress: 0,
        currentStage: 'ocr',
        stageMessage: '正在准备OCR识别...'
      };
      return newEssays;
    });

    // 创建进度管理器
    const progressManager = new ProgressManager();
    progressManagersRef.current.set(essay.id, progressManager);

    progressManager.onProgress((info) => {
      // 更新进度显示，但不改变状态
      onEssaysChange(prevEssays => {
        const newEssays = [...prevEssays];
        newEssays[index] = {
          ...newEssays[index],
          progress: info.progress,
          currentStage: info.stage,
          stageMessage: info.message
        };
        return newEssays;
      });
    });

    try {
      progressManager.startPolling(essay.id);
      await essayAPI.ocr(essay.id);

      // 从后端获取最新状态
      const essayData = await essayAPI.get(essay.id);
      const updatedEssay = essayData.data.essay;

      // 根据后端状态更新前端
      onEssaysChange(prevEssays => {
        const newEssays = [...prevEssays];
        newEssays[index] = {
          ...newEssays[index],
          status: updatedEssay.original_text || updatedEssay.corrected_text ? 'recognized' : mapBackendStatusToFrontend(updatedEssay.status),
          ocrText: updatedEssay.corrected_text || updatedEssay.original_text || '',
          progress: 100
        };
        return newEssays;
      });

      if (updatedEssay.original_text || updatedEssay.corrected_text) {
        message.success('识别完成');
      }
    } catch (error) {
      // 即使失败也要从后端获取最新状态
      try {
        const essayData = await essayAPI.get(essay.id);
        const updatedEssay = essayData.data.essay;

        onEssaysChange(prevEssays => {
          const newEssays = [...prevEssays];
          newEssays[index] = {
            ...newEssays[index],
            status: mapBackendStatusToFrontend(updatedEssay.status),
            error: '识别失败'
          };
          return newEssays;
        });
      } catch {
        // 如果获取状态也失败
        onEssaysChange(prevEssays => {
          const newEssays = [...prevEssays];
          newEssays[index] = {
            ...newEssays[index],
            status: 'failed',
            error: '识别失败'
          };
          return newEssays;
        });
      }
      message.error('识别失败');
    } finally {
      progressManager.stopPolling();
      progressManagersRef.current.delete(essay.id!);
    }
  };

  // 批量OCR识别 - 提交到后端并轮询状态
  const handleBatchOCR = async () => {
    const selectedEssays = essays.filter((essay, index) =>
      selectedRowIds.includes(getEssayRowId(essay, index)) && essay.id && essay.studentId > 0 && essay.fileName &&
      (essay.status === 'uploaded' || essay.status === 'failed')
    );

    if (selectedEssays.length === 0) {
      message.warning('请选择已上传文件的作文');
      return;
    }

    try {
      const essayIds = selectedEssays.filter(e => e.id).map(e => e.id!);
      await essayAPI.batchOCR(essayIds);
      message.success(`已提交${essayIds.length}篇作文的OCR识别任务`);

      // 开始轮询状态
      startPolling();
    } catch (error) {
      message.error('提交OCR任务失败');
    }
  };

  // 单个批改 - 完全依赖后端状态
  const handleCorrect = async (rowId: string) => {
    const index = findEssayIndex(rowId);

    if (index < 0) {
      return;
    }

    const essay = essays[index];

    if (!essay.id || !essay.ocrText) {
      message.error('请先完成文字识别');
      return;
    }

    // 立即更新状态为批改中
    onEssaysChange(prevEssays => {
      const newEssays = [...prevEssays];
      newEssays[index] = {
        ...newEssays[index],
        status: 'correcting',
        progress: 0,
        currentStage: 'analyzing',
        stageMessage: '正在准备批改...'
      };
      return newEssays;
    });

    const progressManager = new ProgressManager();
    progressManagersRef.current.set(essay.id, progressManager);

    progressManager.onProgress((info) => {
      // 只更新进度显示
      onEssaysChange(prevEssays => {
        const newEssays = [...prevEssays];
        newEssays[index] = {
          ...newEssays[index],
          progress: info.progress,
          currentStage: info.stage,
          stageMessage: info.message
        };
        return newEssays;
      });
    });

    try {
      progressManager.startPolling(essay.id);
      await essayAPI.correct(essay.id);

      // 等待后端处理完成
      await new Promise(resolve => setTimeout(resolve, 1000));

      // 从后端获取最新状态
      const essayData = await essayAPI.get(essay.id);
      const updatedEssay = essayData.data.essay;

      // 根据后端状态更新前端
      onEssaysChange(prevEssays => {
        const newEssays = [...prevEssays];
        newEssays[index] = {
          ...newEssays[index],
          status: mapBackendStatusToFrontend(updatedEssay.status),
          progress: updatedEssay.status === 'completed' ? 100 : newEssays[index].progress
        };
        return newEssays;
      });

      if (updatedEssay.status === 'completed') {
        message.success('批改完成');
      } else if (updatedEssay.status === 'failed') {
        throw new Error('批改失败');
      }
    } catch (error) {
      // 即使失败也要从后端获取最新状态
      try {
        const essayData = await essayAPI.get(essay.id);
        const updatedEssay = essayData.data.essay;

        onEssaysChange(prevEssays => {
          const newEssays = [...prevEssays];
          newEssays[index] = {
            ...newEssays[index],
            status: mapBackendStatusToFrontend(updatedEssay.status),
            error: '批改失败'
          };
          return newEssays;
        });
      } catch {
        // 如果获取状态也失败
        onEssaysChange(prevEssays => {
          const newEssays = [...prevEssays];
          newEssays[index] = {
            ...newEssays[index],
            status: 'failed',
            error: '批改失败'
          };
          return newEssays;
        });
      }
      message.error('批改失败');
    } finally {
      progressManager.stopPolling();
      progressManagersRef.current.delete(essay.id);
    }
  };

  // 批量批改 - 提交到后端并轮询状态
  const handleBatchCorrect = async () => {
    const selectedEssays = essays.filter((essay, index) =>
      selectedRowIds.includes(getEssayRowId(essay, index)) && essay.id && essay.studentId > 0 &&
      (essay.status === 'recognized' || (essay.ocrText && essay.status !== 'completed' && essay.status !== 'correcting'))
    );

    if (selectedEssays.length === 0) {
      message.warning('请选择已识别的作文');
      return;
    }

    try {
      const essayIds = selectedEssays.filter(e => e.id).map(e => e.id!);
      await essayAPI.batchCorrect(essayIds);
      message.success(`已提交${essayIds.length}篇作文的批改任务`);

      // 开始轮询状态
      startPolling();
    } catch (error) {
      message.error('提交批改任务失败');
    }
  };

  // 失败后重试批量批改
  const handleRetryBatchCorrect = async (rowId: string) => {
    const index = findEssayIndex(rowId);

    if (index < 0) {
      return;
    }

    const essay = essays[index];

    if (!essay.id) {
      message.error('缺少作文ID，无法重试批改');
      return;
    }

    if (!batchInfo.batchId) {
      message.error('批次信息缺失，请先保存批次');
      return;
    }

    onEssaysChange(prevEssays => {
      const newEssays = [...prevEssays];
      newEssays[index] = {
        ...newEssays[index],
        status: 'correcting',
        progress: 0,
        currentStage: 'correction',
        stageMessage: '重新提交批改任务...',
        error: undefined
      };
      return newEssays;
    });

    try {
      await essayAPI.batchCorrect([essay.id]);
      message.success('已重新提交批改任务');
      startPolling();
    } catch (error) {
      message.error('重试批改提交失败');
      onEssaysChange(prevEssays => {
        const newEssays = [...prevEssays];
        newEssays[index] = {
          ...newEssays[index],
          status: 'failed',
          error: '批改任务提交失败'
        };
        return newEssays;
      });
    }
  };

  // 失败后重试批量识别
  const handleRetryBatchOCR = async (rowId: string) => {
    const index = findEssayIndex(rowId);

    if (index < 0) {
      return;
    }

    const essay = essays[index];

    if (!essay.id) {
      message.error('缺少作文ID，无法重试识别');
      return;
    }

    if (!batchInfo.batchId) {
      message.error('批次信息缺失，请先保存批次');
      return;
    }

    onEssaysChange(prevEssays => {
      const newEssays = [...prevEssays];
      newEssays[index] = {
        ...newEssays[index],
        status: 'recognizing',
        progress: 0,
        currentStage: 'ocr',
        stageMessage: '重新提交识别任务...',
        error: undefined
      };
      return newEssays;
    });

    try {
      await essayAPI.batchOCR([essay.id]);
      message.success('已重新提交识别任务');
      startPolling();
    } catch (error) {
      message.error('重试识别提交失败');
      onEssaysChange(prevEssays => {
        const newEssays = [...prevEssays];
        newEssays[index] = {
          ...newEssays[index],
          status: 'failed',
          error: '识别任务提交失败'
        };
        return newEssays;
      });
    }
  };

  // 开始轮询批次状态
  const startPolling = () => {
    if (!batchInfo.batchId) return;

    // 如果已经在轮询，先停止
    stopPolling();

    // 每2秒查询一次状态
    pollingInterval.current = setInterval(async () => {
      try {
        const response = await essayAPI.getBatchStatus(batchInfo.batchId!);
        const { essays: statusList, summary } = response.data;

        // 更新作文状态
        const updatedEssays = essays.map(essay => {
          const status = statusList.find((s: any) => s.id === essay.id);
          if (status) {
            return {
              ...essay,
              status: mapBackendStatusToFrontend(status.status),
              progress: status.progress,
              error: status.error
            };
          }
          return essay;
        });

        onEssaysChange(updatedEssays);

        // 如果没有处理中的任务，停止轮询
        if (summary.processing === 0) {
          stopPolling();
          if (summary.completed > 0) {
            message.success(`处理完成：成功${summary.completed}篇，失败${summary.failed}篇`);
          }
        }
      } catch (error) {
        console.error('查询批次状态失败:', error);
      }
    }, 2000);
  };

  // 停止轮询
  const stopPolling = () => {
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
      pollingInterval.current = null;
    }
  };

  // 组件卸载时停止轮询
  useEffect(() => {
    // 在 effect 内部保存 ref 引用
    const managersRef = progressManagersRef;

    return () => {
      stopPolling();
      // 使用保存的引用
      const managers = managersRef.current;
      managers.forEach(manager => manager.destroy());
      managers.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 统一补齐稳定 rowId，避免不同入口传入数据时出现行标识不一致。
  useEffect(() => {
    let hasMissingRowId = false;

    const normalizedEssays = essays.map((essay) => {
      if (essay.rowId) {
        return essay;
      }

      hasMissingRowId = true;
      return {
        ...essay,
        rowId: essay.id ? `essay-${essay.id}` : createRowId(),
      };
    });

    if (hasMissingRowId) {
      onEssaysChange(normalizedEssays);
    }
  }, [essays, onEssaysChange]);

  // 当数据行发生变化时，清理已经失效的勾选项。
  useEffect(() => {
    const validRowIds = new Set(
      essays.map((essay, index) => getEssayRowId(essay, index))
    );

    setSelectedRowIds((prevRowIds) => {
      const nextRowIds = prevRowIds.filter((rowId) => validRowIds.has(rowId));
      return nextRowIds.length === prevRowIds.length ? prevRowIds : nextRowIds;
    });
  }, [essays]);

  // 修改作文内容
  const handleEdit = (essay: StudentEssay) => {
    setEditingEssay(essay);
    setEditingText(essay.ocrText || '');
    setEditModalVisible(true);
  };

  // 保存修改
  const handleSaveEdit = async () => {
    if (!editingEssay?.id) return;

    try {
      await essayAPI.update(editingEssay.id, {
        corrected_text: editingText
      });
      
      const index = essays.findIndex(e => e.id === editingEssay.id);
      if (index < 0) {
        message.error('未找到对应的作文记录');
        return;
      }
      const newEssays = [...essays];
      newEssays[index] = {
        ...newEssays[index],
        ocrText: editingText
      };
      onEssaysChange(newEssays);
      
      message.success('保存成功');
      setEditModalVisible(false);
    } catch (error) {
      message.error('保存失败');
    }
  };

  // 查看结果
  const handleViewResult = async (essay: StudentEssay) => {
    if (essay.id) {
      // 先验证批改结果是否存在
      try {
        await essayAPI.get(essay.id);
        // 延迟100ms确保数据已经保存
        setTimeout(() => {
          // 在批量批改页面中，应该在当前窗口打开，不要新开窗口
          // 使用 navigate 而不是 window.open
          navigate(`/essay/${essay.id}/result`);
        }, 100);
      } catch (error) {
        message.error('批改结果尚未准备好，请稍后再试');
      }
    }
  };

  const columns: ColumnsType<StudentEssay> = [
    {
      title: '学生',
      key: 'studentName',
      width: 350,
      sorter: compareText((record: StudentEssay) => record.studentName),
      filters: buildFilters(essays, (record: StudentEssay) => record.studentName, {
        includeEmpty: true,
        emptyText: '未选择学生',
      }),
      onFilter: createValueFilter((record: StudentEssay) => record.studentName),
      filterSearch: true,
      render: (_: unknown, record: StudentEssay) => {
        const rowId = getEssayRowId(record);

        return record.studentId === 0 ? (
          <StudentSelector
            value={{
              studentId: undefined,
              classId: record.classId || defaultClassId
            }}
            onChange={(value) => handleStudentSelect(rowId, value)}
            excludeIds={essays.filter(e => e.studentId > 0).map(e => e.studentId)}
            classes={classes}
            onLoadStudents={loadClassStudents}
          />
        ) : (
          <Space>
            <span>{record.studentName}</span>
            <Button
              size="small"
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleModifyStudent(rowId)}
            />
          </Space>
        );
      },
    },
    {
      title: '班级',
      dataIndex: 'className',
      key: 'className',
      width: 120,
      sorter: compareText((record: StudentEssay) => record.className),
      filters: buildFilters(essays, (record: StudentEssay) => record.className, {
        includeEmpty: true,
        emptyText: '未分配班级',
      }),
      onFilter: createValueFilter((record: StudentEssay) => record.className),
      filterSearch: true,
      render: (className: string) => className || '-',
    },
    {
      title: '作文文件',
      key: 'fileName',
      width: 180,
      sorter: compareText((record: StudentEssay) => record.fileName),
      filters: [
        { text: '已上传文件', value: 'uploaded' },
        { text: '未上传文件', value: 'empty' },
      ],
      onFilter: (value, record) => (
        value === 'uploaded' ? Boolean(record.fileName) : !record.fileName
      ),
      render: (_: unknown, record: StudentEssay) => {
        const rowId = getEssayRowId(record);

        return (
          <Upload
            beforeUpload={(file) => {
              handleFileUpload(rowId, file);
              return false;
            }}
            showUploadList={false}
            disabled={record.studentId === 0}
          >
            <Button icon={<UploadOutlined />} disabled={record.studentId === 0}>
              {record.fileName || '浏览文件'}
            </Button>
          </Upload>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 200,
      sorter: compareEssayStatus((record: StudentEssay) => record.status),
      filters: buildFilters(essays, (record: StudentEssay) => record.status, {
        labelGetter: (value) => getEssayStatusLabel(value),
      }),
      onFilter: createValueFilter((record: StudentEssay) => record.status),
      filterSearch: true,
      render: (_: unknown, record: StudentEssay) => (
        record.status === 'recognizing' || record.status === 'correcting' ? (
          <ProgressDisplay
            percent={record.progress}
            stage={record.currentStage}
            message={record.stageMessage}
            studentName={record.studentName}
          />
        ) : (
          <StatusTag status={record.status} />
        )
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: unknown, record: StudentEssay) => {
        const rowId = getEssayRowId(record);

        return (
          <Space>
            {record.studentId === 0 ? (
              <Popconfirm
                title="确定删除该行吗？"
                onConfirm={() => handleDeleteStudent(rowId)}
              >
                <Button size="small" icon={<DeleteOutlined />} danger />
              </Popconfirm>
            ) : (
              <>
                <Popconfirm
                  title="确定删除该学生吗？"
                  onConfirm={() => handleDeleteStudent(rowId)}
                >
                  <Button size="small" icon={<DeleteOutlined />} danger />
                </Popconfirm>
                {record.status === 'uploaded' && (
                  <Button
                    size="small"
                    icon={<ScanOutlined />}
                    onClick={() => handleOCR(rowId)}
                  >
                    识别
                  </Button>
                )}
                {record.status === 'recognized' && (
                  <>
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => handleEdit(record)}
                    >
                      修改
                    </Button>
                    <Button
                      size="small"
                      type="primary"
                      icon={<CheckCircleOutlined />}
                      onClick={() => handleCorrect(rowId)}
                    >
                      批改
                    </Button>
                  </>
                )}
                {record.status === 'completed' && (
                  <Button
                    size="small"
                    icon={<FileTextOutlined />}
                    onClick={() => handleViewResult(record)}
                  >
                    查看
                  </Button>
                )}
                {record.status === 'failed' && (
                  record.ocrText ? (
                    <>
                      <Tooltip title={record.error || '批改失败，可重试'}>
                        <Button
                          size="small"
                          type="primary"
                          icon={<ReloadOutlined />}
                          onClick={() => handleRetryBatchCorrect(rowId)}
                        >
                          重试批改
                        </Button>
                      </Tooltip>
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                      >
                        修改
                      </Button>
                    </>
                  ) : (
                    <Tooltip title={record.error || '识别失败，可重试'}>
                      <Button
                        size="small"
                        icon={<ReloadOutlined />}
                        onClick={() => handleRetryBatchOCR(rowId)}
                      >
                        重试识别
                      </Button>
                    </Tooltip>
                  )
                )}
              </>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button 
            icon={<PlusOutlined />} 
            onClick={handleAddStudent}
          >
            添加学生
          </Button>
          <Tooltip title="请先勾选已上传文件的学生">
            <Button
              onClick={handleBatchOCR}
              disabled={
                selectedRowIds.length === 0 ||
                !essays.some((essay, index) => (
                  selectedRowIds.includes(getEssayRowId(essay, index)) &&
                  essay.studentId > 0 &&
                  essay.fileName
                ))
              }
            >
              批量识别
            </Button>
          </Tooltip>
          <Tooltip title="请先勾选已识别的作文">
            <Button 
              type="primary"
              onClick={handleBatchCorrect}
              disabled={
                selectedRowIds.length === 0 ||
                !essays.some((essay, index) => (
                  selectedRowIds.includes(getEssayRowId(essay, index)) &&
                  essay.studentId > 0 &&
                  (essay.ocrText || essay.status === 'recognized')
                ))
              }
            >
              批量批改
            </Button>
          </Tooltip>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={essays}
        rowKey={(record, index) => getEssayRowId(record, index)}
        rowSelection={{
          selectedRowKeys: selectedRowIds,
          onChange: (rowKeys) => setSelectedRowIds(rowKeys as string[]),
        }}
        pagination={false}
        scroll={{ x: 1100 }}
      />

      <Modal
        title="修改作文内容"
        visible={editModalVisible}
        width={800}
        onOk={handleSaveEdit}
        onCancel={() => setEditModalVisible(false)}
      >
        <TextArea 
          rows={20}
          value={editingText}
          onChange={e => setEditingText(e.target.value)}
        />
      </Modal>
    </>
  );
};

export default StudentEssayTable;
